import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { 
  ChevronRight, 
  MoreVertical, 
  FilePlus,
  FolderPlus,
  BookOpen,
  Target,
  Instagram,
  Calendar,
  FileIcon,
  FolderIcon,
  Users,
  ArrowLeft,
  Trello
} from 'lucide-react';
import { KnowledgeItem } from '../services/api';
import { ParticipantsIndicator } from './ParticipantsIndicator';

interface KnowledgeFileStructureProps {
  items: KnowledgeItem[];
  selectedItem: KnowledgeItem | null;
  expandedFolders: string[];
  isEditing: string | null;
  editName: string;
  onToggleFolder: (id: string) => void;
  onSelectItem: (item: KnowledgeItem) => void;
  onContextMenu: (e: React.MouseEvent, item: KnowledgeItem) => void;
  onEditNameChange: (value: string) => void;
  onEditSave: (item: KnowledgeItem) => void;
  onAddFile: () => void;
  onAddFolder: () => void;
  onMenuStateChange?: (isOpen: boolean) => void;
  isMenuVisible: boolean;
  movingItems?: Set<string>;
}

export function KnowledgeFileStructure({
  items = [],
  selectedItem,
  expandedFolders,
  isEditing,
  editName,
  onToggleFolder,
  onSelectItem,
  onContextMenu,
  onEditNameChange,
  onEditSave,
  onAddFile,
  onAddFolder,
  onMenuStateChange,
  isMenuVisible,
  movingItems = new Set()
}: KnowledgeFileStructureProps) {
  // Значения ширины в пикселях
  const defaultWidth = 300; // Ширина по умолчанию (70 в tailwind примерно равно 280px)
  const tabletWidth = 300; // Ширина для планшетов
  const minWidth = 200; // Минимальная ширина панели
  
  // Устанавливаем начальную ширину из localStorage или используем значение по умолчанию
  const [width, setWidth] = useState(() => {
    const savedWidth = localStorage.getItem('knowledgePanelWidth');
    return savedWidth ? parseInt(savedWidth, 10) : defaultWidth;
  });
  
  // Состояние для отслеживания процесса ресайза
  const [isResizing, setIsResizing] = useState(false);
  
  // Состояние для мобильного меню
  // const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const viewModeInitialized = useRef(false);

  useEffect(() => {
    const checkViewMode = () => {
      const width = window.innerWidth;
      let newViewMode: 'mobile' | 'tablet' | 'desktop';
      
      if (width < 768) {
        newViewMode = 'mobile';  // Мобильные устройства не должны показывать выезжающее меню
      } else if (width < 1024) {
        newViewMode = 'tablet';  // Планшеты используют выезжающее меню
      } else {
        newViewMode = 'desktop'; // Десктоп всегда показывает меню
      }
      
      setViewMode(newViewMode);
      
      if (!viewModeInitialized.current) {
        viewModeInitialized.current = true;
      }
    };
    checkViewMode();
    window.addEventListener('resize', checkViewMode);
    return () => window.removeEventListener('resize', checkViewMode);
  }, []);

  // Ссылка на инпут, чтобы сфокусироваться на нем при появлении
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const currentWidthRef = useRef(width);

  // Инициализация CSS-переменной при монтировании и изменении ширины
  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.style.setProperty('--panel-width', `${width}px`);
      currentWidthRef.current = width;
    }
  }, [width]);

  // Обработчик начала перетаскивания
  const startResizing = useCallback((e: React.MouseEvent) => {
    if (viewMode !== 'desktop') return; // Disable resizing on mobile and tablet
    e.preventDefault();
    e.stopPropagation(); // Добавляем для предотвращения конфликтов
    startXRef.current = e.clientX;
    currentWidthRef.current = width;
    setIsResizing(true);
    
    // Добавляем класс для блокировки плавной анимации при перетаскивании
    if (panelRef.current) {
      panelRef.current.classList.add('resizing');
    }
    
    // Добавляем класс к body для изменения курсора
    document.body.classList.add('resize-cursor');
    

  }, [width, viewMode]);

  // Оптимизированный обработчик перетаскивания
  useEffect(() => {
    const handleResize = (e: MouseEvent) => {
      if (!isResizing || !panelRef.current) return;
      
      e.preventDefault();
      
      const deltaX = e.clientX - startXRef.current;
      let newWidth = currentWidthRef.current + deltaX;
      
      // Применяем ограничения
      if (newWidth < minWidth) newWidth = minWidth;
      const maxWidth = Math.min(window.innerWidth * 0.4, 500); // Увеличиваем максимальную ширину
      if (newWidth > maxWidth) newWidth = maxWidth;
      
      // Применяем новую ширину напрямую к элементу
      panelRef.current.style.width = `${newWidth}px`;
      

    };

    const stopResizing = () => {
      if (!isResizing || !panelRef.current) return;
      
      panelRef.current.classList.remove('resizing');
      document.body.classList.remove('resize-cursor');
      
      const currentWidth = parseInt(panelRef.current.style.width || `${width}px`, 10);

      
      setWidth(currentWidth);
      localStorage.setItem('knowledgePanelWidth', String(currentWidth));
      setIsResizing(false);
    };

    if (isResizing && viewMode === 'desktop') {
      document.addEventListener('mousemove', handleResize);
      document.addEventListener('mouseup', stopResizing);
      
      // Предотвращаем выделение текста во время resize
      document.addEventListener('selectstart', (e) => e.preventDefault());
    }

    return () => {
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', stopResizing);
      document.removeEventListener('selectstart', (e) => e.preventDefault());
      document.body.classList.remove('resize-cursor');
    };
  }, [isResizing, minWidth, viewMode, width]);

  // Эффект для фокуса на инпуте при редактировании
  useEffect(() => {
    if (isEditing && inputRef.current) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 50);
    }
  }, [isEditing]);
  
  // Сохраняем ширину при размонтировании компонента
  useEffect(() => {
    return () => {
      localStorage.setItem('knowledgePanelWidth', String(width));
    };
  }, [width]);

  // Функция для обработки клика по элементу
  const handleItemClick = useCallback((item: KnowledgeItem) => {
    // Сначала выбираем элемент
    onSelectItem(item);

    // Затем закрываем меню на планшетах с небольшой задержкой
    // чтобы пользователь видел, что элемент выбран
    if (viewMode === 'tablet' && isMenuVisible) {
      setTimeout(() => {
        onMenuStateChange?.(false);
      }, 150); // Задержка 150ms для плавного UX
    }
  }, [onSelectItem, viewMode, onMenuStateChange, isMenuVisible]);
  
  // Функция для проверки, является ли элемент выбранным
  const isItemSelected = useCallback((itemId: string): boolean => {
    return itemId === selectedItem?.id;
  }, [selectedItem]);

  const getFileIcon = (fileType?: string) => {
    switch (fileType) {
      case 'article':
      case 'summary':
        return <FileIcon className="w-3.5 h-3.5 mr-1.5 text-blue-500" strokeWidth={1.5} />;
      case 'notes':
        return <BookOpen className="w-3.5 h-3.5 mr-1.5 text-purple-500" strokeWidth={1.5} />;
      case 'roadmap':
        return <Target className="w-3.5 h-3.5 mr-1.5 text-orange-500" strokeWidth={1.5} />;
      case 'reels':
        return <Instagram className="w-3.5 h-3.5 mr-1.5 text-rose-500" strokeWidth={1.5} />;
      case 'content-plan':
        return <Calendar className="w-3.5 h-3.5 mr-1.5 text-teal-500" strokeWidth={1.5} />;
      case 'audience':
        return <Users className="w-3.5 h-3.5 mr-1.5 text-indigo-500" strokeWidth={1.5} />;
      case 'kanban':
        return <Trello className="w-3.5 h-3.5 mr-1.5 text-red-500" strokeWidth={1.5} />;
      default:
        return <FileIcon className="w-3.5 h-3.5 mr-1.5 text-gray-500" strokeWidth={1.5} />;
    }
  };

  // Функция для сортировки элементов по дате создания
  const sortItemsByDate = useCallback((itemsToSort: KnowledgeItem[]): KnowledgeItem[] => {
    return [...itemsToSort].sort((a, b) => {
      // Получаем даты создания
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      
      // Сортируем по убыванию (новые внизу)
      return dateA - dateB;
    });
  }, []);

  // Сортируем элементы по дате создания
  const sortedItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    
    // Фильтруем валидные элементы и сортируем их
    const validItems = items.filter(item => item && item.id);
    return sortItemsByDate(validItems);
  }, [items, sortItemsByDate]);

  const renderItem = useCallback((item: KnowledgeItem, level: number = 0) => {
    if (!item || !item.id) {
      return null;
    }

    const isExpandedFolder = expandedFolders.includes(item.id);
    // Проверяем, является ли элемент выбранным
    const selected = isItemSelected(item.id);
    
    // Определяем, находится ли ИМЕННО этот элемент в режиме редактирования
    const isItemEditing = isEditing === item.id;
    
    const isTemporary = item.id.startsWith('temp-');
    
    // Логируем только временные элементы
    if (isTemporary) {
      console.log('[KnowledgeFileStructure] Рендерим временный элемент:', item.id, 'name:', item.name, 'isItemEditing:', isItemEditing);
    }
    
    // Изменяем логику определения отображаемого имени для временных элементов
    let displayName = item.name || "";
    if (isTemporary && !item.name) {
      // Определяем тип временного элемента по префиксу ID
      if (item.id.startsWith('temp-folder-')) {
        displayName = "Новая папка";
      } else {
        displayName = "Новый файл";
      }
    } else {
      displayName = item.name || "Без имени";
    }
    
    // Проверяем, есть ли у элемента дочерние элементы
    const hasChildren = item.children && item.children.length > 0;
    
    // Для временных элементов поле ввода показывается только при isItemEditing
    const shouldShowInput = isItemEditing;
    
    // Для временных элементов используем editName, если он есть, иначе item.name
    const inputValue = editName || item.name || '';
    
    // Логируем детали для временных элементов
    if (isTemporary) {
      console.log('[KnowledgeFileStructure] Детали временного элемента:', {
        id: item.id,
        name: item.name,
        isItemEditing,
        shouldShowInput,
        editName,
        inputValue,
        displayName
      });
    }
    
    // Проверяем, перемещается ли элемент
    const isMoving = movingItems.has(item.id);
    
    return (
      <React.Fragment key={item.id}>
        <div
          className={`group flex items-center py-1 px-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg cursor-pointer truncate ${
            selected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
          } ${viewMode !== 'desktop' ? 'active:bg-gray-100 dark:active:bg-gray-700' : ''} ${
            isMoving ? 'opacity-50 bg-yellow-50 dark:bg-yellow-900/20' : ''
          }`}
          style={{ paddingLeft: `${level * 0.75 + 0.5}rem` }}
          onClick={(e) => {
            e.preventDefault(); // Предотвращаем дефолтное поведение
            e.stopPropagation(); // Останавливаем всплытие события
            
            if (isTemporary) {
              // Для временных элементов НЕ вызываем onSelectItem
              // Вместо этого сразу активируем редактирование
              // Для временных элементов закрываем меню сразу на планшетах
              if (viewMode === 'tablet' && isMenuVisible) {
                setTimeout(() => {
                  onMenuStateChange?.(false);
                }, 100);
              }
              // Для временных элементов активируем редактирование при клике
              if (!isEditing) {
                // Активируем редактирование для временного элемента
                onEditNameChange(item.name || '');
                // Вызываем контекстное меню для активации редактирования
                onContextMenu(e, item);
              }
            } else if (item.itemType === 'folder') {
              // Для папок: только переключаем раскрытие, НЕ выбираем
              onToggleFolder(item.id);
            } else {
              // Для файлов используем handleItemClick
              handleItemClick(item);
            }
          }}
          onContextMenu={(e) => {
            // Для временных элементов в режиме редактирования не показываем контекстное меню
            if (isTemporary && isEditing) {
              return;
            }
            onContextMenu(e, item);
          }}
        >
          {(item.itemType === 'folder' || hasChildren) && (
            <div 
              className="relative flex items-center justify-center w-4 mr-1"
              onClick={(e) => {
                e.stopPropagation();
                // Клик по стрелке всегда только переключает раскрытие
                onToggleFolder(item.id);
              }}
            >
              <ChevronRight
                className={`w-3.5 h-3.5 transition-transform text-gray-400 dark:text-gray-300 ${isExpandedFolder ? 'transform rotate-90' : ''}`}
                strokeWidth={1.5}
              />
            </div>
          )}
          {item.itemType === 'folder' ? (
            <div className="flex items-center">
              <FolderIcon className="w-3.5 h-3.5 mr-1.5 text-blue-500" strokeWidth={1.5} />
              {isMoving && (
                <div className="animate-spin rounded-full h-3 w-3 border border-yellow-500 border-t-transparent mr-1"></div>
              )}
            </div>
          ) : (
            <div className="flex items-center">
              <div className="mr-1.5">
                {getFileIcon(item.fileType)}
              </div>
              {isMoving && (
                <div className="animate-spin rounded-full h-3 w-3 border border-yellow-500 border-t-transparent mr-1"></div>
              )}
              <ParticipantsIndicator 
                knowledgeItemId={item.id} 
                fileType={item.fileType}
                isShared={item.isShared}
                knowledgeItem={item}
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            {shouldShowInput ? (
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => onEditNameChange(e.target.value)}
                onBlur={() => {
                  // Для временных элементов сохраняем только если введено непустое название
                  if (isTemporary) {
                    const trimmedName = editName.trim();
                    if (trimmedName) {
                      onEditSave(item);
                    }
                    // Если название пустое, не делаем ничего
                    // Временный элемент остается видимым
                  } else {
                    onEditSave(item);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    // Для временных элементов сохраняем только если введено непустое название
                    if (isTemporary) {
                      const trimmedName = editName.trim();
                      if (trimmedName) {
                        onEditSave(item);
                      }
                      // Если название пустое, не делаем ничего
                      // Временный элемент остается видимым
                    } else {
                      onEditSave(item);
                    }
                  } else if (e.key === 'Escape') {
                    if (item.id.startsWith('temp-')) {
                      // Для временных элементов при Escape удаляем их
                      onEditNameChange('');
                      onEditSave(item);
                    } else {
                      // Для существующих элементов возвращаем исходное имя
                      onEditNameChange(item.name || '');
                      onEditSave(item);
                    }
                  }
                }}
                className="w-full bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                placeholder="Введите имя"
                autoFocus={isTemporary} // Автофокус только для временных элементов
                onFocus={(e) => e.target.select()}
              />
            ) : (
              <span className="text-gray-900 dark:text-white truncate text-sm">
                {displayName}
              </span>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onContextMenu(e, item);
            }}
            className={`p-1 rounded-full transition-all flex-shrink-0 ml-auto ${
              viewMode === 'tablet' 
                ? 'opacity-100 active:bg-gray-200 dark:active:bg-gray-600' 
                : 'opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <MoreVertical className="w-3 h-3 text-gray-500 dark:text-gray-400" strokeWidth={1.5} />
          </button>
        </div>
        {isExpandedFolder && hasChildren && item.children && (
          <div>
            {sortItemsByDate(item.children.filter(child => child && child.id)).map(child => renderItem(child, level + 1))}
          </div>
        )}
      </React.Fragment>
    );
  }, [expandedFolders, isEditing, editName, onToggleFolder, onSelectItem, onContextMenu, isItemSelected, handleItemClick, viewMode, onMenuStateChange, sortItemsByDate]);

  // Определяем классы для панели в зависимости от состояния
  const panelClasses = [
    'flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 knowledge-panel',
    viewMode === 'desktop'
      ? `h-full relative ${isMenuVisible ? 'flex' : 'hidden'}` // Используем flex для показа
      : `fixed top-0 left-0 z-50 ${isMenuVisible ? 'mobile-visible' : 'mobile-hidden'}` // Увеличиваем z-index до 50
  ].join(' ');

  const panelStyle: React.CSSProperties = {
    width: viewMode === 'desktop'
      ? `${width}px`
      : `${tabletWidth}px`, // Используем ширину планшета для выезжающего меню
    height: viewMode === 'tablet' ? '100vh' : 'auto', // Полная высота для планшетов
    top: viewMode === 'tablet' ? '0' : 'auto'
  };

  const panelContent = (
    <>
        {/* Resizer */}
        {viewMode === 'desktop' && (
          <div
            data-resizer="true"
            onMouseDown={startResizing}
            className="absolute top-0 right-0 w-4 h-full cursor-col-resize opacity-0 hover:opacity-30 transition-opacity duration-200 z-30 flex items-center justify-center"
            style={{ 
              background: 'transparent'
            }}
            title="Перетащите для изменения ширины панели"
          >
            {/* Визуальный индикатор - три точки */}
            <div className="flex flex-col space-y-1">
              <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full" />
              <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full" />
              <div className="w-1 h-1 bg-gray-400 dark:bg-gray-500 rounded-full" />
            </div>
          </div>
        )}

        {/* Header section */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Файлы</h2>
            <div className="flex items-center space-x-2">
              {false && (
                <button 
                  onClick={onAddFile} 
                  title="Добавить файл"
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <FilePlus className="w-4 h-4 text-blue-500 dark:text-blue-400" strokeWidth={1.5} />
                </button>
              )}
              <button 
                onClick={onAddFolder} 
                title="Добавить папку"
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <FolderPlus className="w-4 h-4 text-blue-500 dark:text-blue-400" strokeWidth={1.5} />
              </button>
            </div>
        </div>

        {/* File list section */}
        <div className="flex-grow overflow-y-auto p-2 space-y-0.5 custom-scrollbar pb-24">
            {sortedItems.map(item => renderItem(item))}
        </div>
    </>
  );

  return (
    <>
      {/* Overlay для планшетов */}
      {viewMode === 'tablet' && isMenuVisible && (
        <div 
          className="fixed inset-0 bg-black/20 z-40" // Увеличиваем z-index до 40
          onClick={() => onMenuStateChange?.(false)}
        />
      )}
      
      {/* Кнопка закрытия вне меню для планшетов */}
      {viewMode === 'tablet' && isMenuVisible && (
        <div className="fixed top-4 right-4 z-60"> {/* Увеличиваем z-index до 60 */}
          <button 
            onClick={() => onMenuStateChange?.(false)} 
            className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 active:bg-gray-50 dark:active:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" strokeWidth={2} />
          </button>
        </div>
      )}
      
      <div ref={panelRef} className={panelClasses} style={panelStyle}>
        {panelContent}
      </div>
    </>
  );
}
